import { value } from '@takeshape/util';
import isString from 'lodash/isString.js';
import { branding } from "../config.js";
import { hasEveryKey } from "../util/has.js";
const serviceProvider = {
    name: 'auth0',
    title: 'Auth0',
    description: `Auth0 provides authentication as a service. See our docs on using auth0 with your ${branding.productName} project at the link above.`,
    categories: ['authentication'],
    links: [
        { title: 'Homepage', url: 'https://auth0.com/' },
        { title: 'Docs', url: '/docs/services/providers/auth0' }
    ],
    serviceType: 'authentication',
    createId: value('auth0'),
    getFormSchema: value({
        type: 'object',
        properties: {
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '[0-9A-Za-z_-]+',
                default: 'auth0'
            },
            title: {
                title: 'Name',
                type: 'string',
                default: 'Auth0'
            },
            domain: {
                title: 'Domain',
                type: 'string',
                pattern: '(?=^.{4,253}$)(^((?!-)[a-zA-Z0-9-]{0,62}[a-zA-Z0-9].)+[a-zA-Z]{2,63}$)'
            },
            audience: {
                title: 'Audience',
                type: 'string'
            }
        },
        required: ['id', 'title', 'domain']
    }),
    getFormConfig: value({
        order: ['title', 'domain', 'audience'],
        properties: {
            audience: {
                widget: 'readonly',
                instructions: 'Click to copy, and then set as the audience in your Auth0 application.',
                copy: true
            },
            title: {
                widget: 'singleLineText',
                instructions: 'Human-readable identifier for use in the UI.'
            },
            domain: {
                widget: 'singleLineText',
                instructions: 'An Auth0 authentication domain.'
            }
        }
    }),
    getConfig: (formData) => ({
        id: formData.id ?? 'auth0',
        title: formData.title ?? 'Auth0',
        provider: 'auth0',
        serviceType: 'authentication',
        authenticationType: 'none',
        options: {
            domain: formData.domain,
            audience: formData.audience
        }
    }),
    getAuthJwtRequiredClaims: value(['sub']),
    getAuthIdClaim(_config, payload) {
        if (!payload.sub) {
            throw new Error('Invalid JWT payload, no sub found');
        }
        return payload.sub;
    },
    getAuthOpenIdConfig: (config) => ({
        jwks_uri: `https://${config.options?.domain}/.well-known/jwks.json`
    }),
    getAuthJwtVerifyConfig: ({ projectId }, config) => ({
        audience: `https://api.takeshape.io/project/${projectId}/${config.id}`,
        issuer: `https://${config.options?.domain}/`,
        algorithms: ['RS256']
    }),
    getAuthUserinfoEndpoint(_config, _openidConfig, payload) {
        const { aud } = payload;
        if (!aud) {
            return;
        }
        const audience = isString(aud) ? [aud] : aud;
        return audience.find((a) => a.includes('userinfo'));
    },
    getAuthAdditionalClaimsConfig(_config, userinfoUri, rawToken) {
        return {
            url: userinfoUri,
            headers: {
                authorization: `Bearer ${rawToken}`
            }
        };
    },
    isCompleteConfig: hasEveryKey(['options.domain', 'options.audience'])
};
export default serviceProvider;
